// Events usage to perform filtering from anywhere
// To filter, use:
// - document.dispatchEvent(new CustomEvent('product-slider:filter', { detail: { modelSize: 'M', modelName: 'Name' } }));
//    Available filter combinations:
//    * Size and name: { modelSize: 'M', modelName: 'Name' }
//    * Size only (any name): { modelSize: 'M' }
//    * Name only (any size): { modelName: 'Name' }
//    * Size without name { modelSize: 'M', modelName: '' }
//    * Name without size { modelSize: '', modelName: 'Name' }
// To reset filters, use:
// - document.dispatchEvent(new CustomEvent('product-slider:reset'));

(() => {
  const callback = () => {
    const selectors = {
      gallery: '.product-section__gallery',
      galleryFlickityContainer: '[data-product-photos]',
      galleryFlickitySlider: '.flickity-slider',
      galleryImage: '.product-main-slide',
      galleryThumbnail: '.product__thumb-item',
      galleryArrow: '.product__thumb-arrow',
      filterWrapper: '.gallery-filter',
      filterTrigger: '.gallery-filter__trigger',
      filterTriggerTitle: '.gallery-filter__trigger-title',
      filterDropdown: '.gallery-filter__dropdown',
      filterSizeSelector: '.gallery-filter__size-selector',
    };

    const gallery = document.querySelector(selectors.gallery);
    if (!gallery) return;

    const galleryFlickityContainer = gallery.querySelector(selectors.galleryFlickityContainer);
    const galleryArrows = gallery.querySelectorAll(selectors.galleryArrow);
    const galleryImages = gallery.querySelectorAll(selectors.galleryImage);
    const galleryThumbnails = gallery.querySelectorAll(selectors.galleryThumbnail);
    const filterWrapper = gallery.querySelector(selectors.filterWrapper);
    const filterTrigger = gallery.querySelector(selectors.filterTrigger);
    const filterTriggerTitle = gallery.querySelector(selectors.filterTriggerTitle);
    const filterDropdown = gallery.querySelector(selectors.filterDropdown);

    const hasMissingElements = [
      galleryFlickityContainer,
      galleryArrows,
      galleryImages,
      galleryThumbnails,
      filterWrapper,
      filterTrigger,
      filterTriggerTitle,
      filterDropdown,
    ].some((element) => !element);
    if (hasMissingElements) return;

    const imageGroups = Array.from(galleryImages).reduce((group, image) => {
      const { modelSize, modelName } = image.dataset;
      const key = [modelSize, modelName].filter(Boolean).join('-') ?? '';

      group[key] = group[key] || { images: [] };
      group[key].modelSize = modelSize;
      group[key].modelName = modelName;
      group[key].images.push(image);

      return group;
    }, {});

    const hasGroupsToFilter = Object.keys(imageGroups).length > 1;
    if (!hasGroupsToFilter) {
      filterWrapper.classList.add('hidden');
      return;
    }

    const { locale } = window.themeComponents.snippets['gallery-filter-ds'] ?? {};

    const mapFilterSelector = ([, { modelName, modelSize }]) => {
      const label = [modelName, modelSize].filter(Boolean).join(', ');

      return `
        <li class="gallery-filter__size-selector">
          <label class="text-label">
            <input 
              type="radio"
              class="sr-only"
              name="gallery-filter"
              value="${label || locale.filterChoose}" 
              data-model-name="${modelName}" 
              data-model-size="${modelSize}"
              tabindex="-1"
              ${!label ? 'checked' : ''}
            >
            <span>${label || locale.filterAll}</span>
          </label>
        </li>
      `;
    };

    const sizesOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', ''];
    const sortedImageGroupsEntries = Object.entries(imageGroups).sort(
      ([, a], [, b]) => sizesOrder.indexOf(a.modelSize) - sizesOrder.indexOf(b.modelSize),
    );
    const dropdownContent = sortedImageGroupsEntries.map(mapFilterSelector).join('');
    filterDropdown.innerHTML = dropdownContent;
    const filterDropdownInputs = filterDropdown.querySelectorAll('input');

    filterWrapper.style.setProperty('--longest-title-width', `${filterWrapper.offsetWidth}px`);

    const toggleAccessibilityAttributes = ({ isActive }) => {
      filterTrigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      filterDropdown.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      filterDropdownInputs.forEach((input) => input.setAttribute('tabindex', isActive ? '0' : '-1'));
    };

    filterWrapper.addEventListener('click', (event) => {
      if (event.target.closest(selectors.filterTrigger)) {
        const isActive = filterWrapper.classList.toggle('active');
        toggleAccessibilityAttributes({ isActive });
      }
    });

    filterWrapper.addEventListener('change', (event) => {
      if (event.target.closest(selectors.filterSizeSelector)) {
        const { modelSize, modelName } = event.target.dataset;

        filterTriggerTitle.innerHTML = event.target.value;
        modelSize || modelName ? filterGallery({ modelSize, modelName }) : resetGallery();
      }
    });

    document.addEventListener('click', (event) => {
      const isClickInside = [filterTrigger, filterDropdown].some((element) => element.contains(event.target));
      if (!isClickInside) {
        filterWrapper.classList.remove('active');
        toggleAccessibilityAttributes({ isActive: false });
      }
    });

    const toggleArrowsVisibility = () => {
      const slidesTotal = Flickity.data(galleryFlickityContainer)?.slides?.length ?? 0;
      const [arrowPrevious, arrowNext] = galleryArrows;

      if (arrowPrevious) arrowPrevious.classList.add('hide');
      if (arrowNext) arrowNext.classList.toggle('hide', slidesTotal <= 1);
    };

    const filterGallery = ({ modelSize, modelName }) => {
      const sizeValue = modelSize ?? event?.detail?.modelSize;
      const nameValue = modelName ?? event?.detail?.modelName;

      const imagesToShow = Object.values(imageGroups)
        .filter((group) => {
          if (sizeValue !== undefined && nameValue !== undefined)
            return group.modelSize === sizeValue && group.modelName === nameValue;
          if (sizeValue) return group.modelSize === sizeValue;
          if (nameValue) return group.modelName === nameValue;
          return false;
        })
        .flatMap((group) => group.images);

      if (!imagesToShow.length) return;

      resetGallery();

      galleryImages.forEach((image) => {
        if (!imagesToShow.includes(image)) {
          const thumbnail = gallery.querySelector(`${selectors.galleryThumbnail}[data-index='${image.dataset.index}']`);
          [image, thumbnail].forEach((element) => element.classList.add('hidden'));
        }
      });

      document.dispatchEvent(
        new CustomEvent('product-slider:init', {
          detail: { customOptions: { wrapAround: false } },
        }),
      );

      toggleArrowsVisibility();
    };

    const resetGallery = () => {
      [...galleryImages, ...galleryThumbnails].forEach((element) => element.classList.remove('hidden'));

      Flickity.data(galleryFlickityContainer)?.destroy();

      // Restore the initial slides order to reintegrate slides omitted by filters
      // as Flickity moves them out of the slider during initialization
      [...galleryFlickityContainer.children]
        .sort((a, b) => a.dataset.index - b.dataset.index)
        .forEach((node) => galleryFlickityContainer.appendChild(node));

      document.dispatchEvent(
        new CustomEvent('product-slider:init', {
          detail: { customOptions: { wrapAround: true } },
        }),
      );

      toggleArrowsVisibility();
    };

    document.addEventListener('product-slider:filter', filterGallery);
    document.addEventListener('product-slider:reset', resetGallery);
  };

  window.addEventListener('DOMContentLoaded', callback);
})();
